/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Text, Link } from '@bufferapp/components';
import { Tooltip, Text as UiText } from '@bufferapp/ui';
import Button from '@bufferapp/ui/Button';
import AnalyzeIcon from '@bufferapp/ui/Icon/Icons/Analyze';
import InfoIcon from '@bufferapp/ui/Icon/Icons/Info';
import { borderWidth } from '@bufferapp/components/style/border';
import { abbreviateNumber } from '~/server/formatters';
import styled from 'styled-components';
import {
  gray,
  grayDark,
  grayLight,
  grayLighter,
} from '@bufferapp/ui/style/colors';
import { useAccount } from '~/accountContext';
import { HC_UTM_PARAMS } from '~/utils/contants';

const StatsTitle = styled(Text)`
  span {
    color: ${grayDark};
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
  }
`;

const StatsValue = styled(Text)`
  span {
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  background-color: ${grayLighter};
  border-top: ${borderWidth} solid ${grayLight};
  box-sizing: border-box;
  padding: 0px 16px;
`;

const StatsContainerStyled = styled.div`
  flex-grow: 1;
  flex-basis: 0;
  display: flex;
  padding: 8px;
  align-items: left;
  flex-direction: column;
`;

const TooltipWrapper = styled.div`
  display: inline-block;
  padding: 0 4px;
  bottom: -4px;
  position: relative;
`;

const TooltipMessage = ({ label }: { label: string | null }) => {
  return label ? (
    <TooltipWrapper>
      <Tooltip label={label} position="right">
        <InfoIcon color={gray} />
      </Tooltip>
    </TooltipWrapper>
  ) : null;
};

const ExternalLink = ({ link }: { link: string | null }) => {
  return link ? (
    <Link href={link} unstyled>
      *
    </Link>
  ) : null;
};

const StatisticElement = ({
  value = null,
  title = '',
  tooltip = null,
  link = null,
}: {
  value?: string | number | null;
  title?: string;
  tooltip?: string | null;
  link?: string | null;
}) => {
  if (typeof value === 'undefined' || value === null) {
    return null;
  }

  return (
    <StatsContainerStyled>
      <StatsTitle>
        <UiText>{title}</UiText>
        {tooltip && <TooltipMessage label={tooltip} />}
        {link && <ExternalLink link={link} />}
      </StatsTitle>
      <StatsValue>
        <UiText>{abbreviateNumber(value, 1)}</UiText>
      </StatsValue>
    </StatsContainerStyled>
  );
};

StatisticElement.defaultProps = {
  value: null,
  title: '',
  tooltip: null,
  link: null,
};

const RedirectToAnalyzeWrapper = styled.div`
  position: absolute;
  display: flex;
  width: 490px;
  right: 16px;
  bottom: 6px;
  align-items: center;
  justify-content: center;

  button {
    font-weight: 600;
    height: 28px;
    font-size: 12px;
  }
`;

const RedirectToAnalyze: React.FC<{
  profileId: string;
  profileService: string;
}> = ({ profileId, profileService }) => {
  return (
    <RedirectToAnalyzeWrapper>
      <Button
        fullWidth
        type="secondary"
        icon={<AnalyzeIcon />}
        label="Open Advanced Analytics for these metrics"
        onClick={() => {
          window.location.href = `https://analyze.buffer.com/${profileService}/posts/${profileId}`;
        }}
      />
    </RedirectToAnalyzeWrapper>
  );
};

const PostStats = ({
  statistics = {},
  profileService,
  profileId,
  showTwitterMentions,
  profileServiceType = null,
  hasAnalyticsOnPosts,
}: {
  statistics: {
    [key: string]: number | string;
  };
  profileService: string;
  profileId: string;
  showTwitterMentions: boolean;
  profileServiceType: string | null;
  hasAnalyticsOnPosts: boolean;
}) => {
  const { account } = useAccount();
  const [isOneBufferOrg, setIsOneBufferOrg] = useState(false);
  useEffect(() => {
    if (account?.currentOrganization?.billing) {
      setIsOneBufferOrg(account.currentOrganization?.isOneBufferOrganization);
    }
  });

  const titles = {
    views: 'View',
    retweets: 'Retweet',
    comments: 'Comment',
    likes: 'Like',
    favorites: 'Like',
    mentions: 'Mention',
    clicks: 'Click',
    reach_twitter: 'Impressions',
    reach: 'Reach',
    shares: 'Share',
    reshares: 'Reshare',
    repins: 'Save',
    plusOne: '+1',
    linkClicks: 'Link Clicks',
  };

  // The order on the following list will be the order of the stats displayed
  const titlesPerNetwork = {
    youtube: {
      views: 'View',
      likes: 'Like',
      comments: 'Comment',
    },
  };

  const advancedStatistics = {
    instagram: [
      {
        title: 'Likes',
        key: 'likes',
      },
      {
        title: 'Comments',
        key: 'comments',
      },
      {
        title: 'Impressions',
        key: 'impressions',
      },
      {
        title: 'Reach',
        key: 'reach',
      },
      {
        title: 'Eng. Rate',
        key: 'engagement_rate',
      },
    ],
    twitter: [
      {
        title: 'Clicks',
        key: 'url_clicks',
      },
      {
        title: 'Retweets',
        key: 'retweets',
      },
      {
        title: 'Likes',
        key: 'favorites',
      },
      {
        title: 'Impressions',
        key: 'impressions',
      },
      {
        title: 'Eng. Rate',
        key: 'engagement_rate',
      },
    ],
    facebook: [
      {
        title: 'Clicks',
        key: 'clicks',
      },
      {
        title: 'Reactions',
        key: 'reactions',
      },
      {
        title: 'Impressions',
        key: 'impressions',
      },
      {
        title: 'Reach',
        key: 'reach',
      },
      {
        title: 'Eng. Rate',
        key: 'engagement_rate',
      },
    ],
    startPage: [
      {
        title: 'Link Clicks',
        key: 'linkClicks',
      },
    ],
  };

  function getMetrics() {
    if (
      isOneBufferOrg &&
      Object.keys(advancedStatistics).includes(profileService)
    ) {
      return advancedStatistics[profileService].map((metric) => {
        let value: string | number = statistics[metric.key] || '-';
        if (metric.key === 'engagement_rate' && typeof value === 'number') {
          value = `${value.toFixed(2)}%`;
        }

        return {
          ...metric,
          value,
          tooltip:
            profileService === 'instagram' && profileServiceType === 'profile'
              ? `Instagram has deprecated statistics for non business accounts. Set up direct scheduling to start seeing these statistics from Instagram Business.`
              : null,
        };
      });
    }

    const metricTitles = titlesPerNetwork[profileService] || titles;
    return Object.keys(metricTitles)
      .map((typeStat) => {
        return [typeStat, metricTitles[typeStat]];
      })
      .map(([typeStat, title]) => {
        if (profileService === 'twitter') {
          if (
            typeStat === 'reach' ||
            (!showTwitterMentions && typeStat === 'mentions')
          ) {
            return null;
          }
          if (typeStat === 'clicks') {
            return null;
          }
        }
        let value = statistics[typeStat];
        if (typeStat === 'reach_twitter' && profileService === 'twitter') {
          value = statistics.impressions;
        }
        const nonPluralTitles = ['reach', 'plusOne', 'reach_twitter'];
        if (value !== 1 && nonPluralTitles.indexOf(typeStat) < 0) {
          title += 's';
        }
        if (typeof value === 'undefined') {
          return null;
        }

        return {
          key: typeStat,
          value,
          title,
          tooltip:
            profileService === 'instagram' && profileServiceType === 'profile'
              ? `Instagram has deprecated statistics for non business accounts. Set up direct scheduling to start seeing these statistics from Instagram Business.`
              : null,
          link: !(typeStat === 'clicks' && profileService === 'linkedin')
            ? null
            : `https://support.buffer.com/hc/en-us/articles/360037901594-Insights-on-shared-posts#h_9575ebf2-62d6-4f13-9f82-d4ccaa6f880d?${HC_UTM_PARAMS}`,
        };
      });
  }

  function shouldShowRedirect(stat) {
    return (
      hasAnalyticsOnPosts &&
      stat.value === '-' &&
      stat.key === 'engagement_rate'
    );
  }

  const metrics = getMetrics();
  return (
    <StatsContainer>
      {metrics.map((stat) => {
        if (stat === null) {
          return null;
        }
        return (
          <>
            {shouldShowRedirect(stat) && (
              <RedirectToAnalyze
                profileId={profileId}
                profileService={profileService}
              />
            )}
            <StatisticElement
              key={stat.key}
              value={!hasAnalyticsOnPosts ? '-' : stat.value}
              title={stat.title}
              tooltip={stat.tooltip}
              link={stat.link}
            />
          </>
        );
      })}
    </StatsContainer>
  );
};

export default PostStats;
